import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Alert,
    InputAdornment,
    Link,
    CircularProgress,
} from '@mui/material';
import {
    Email as EmailIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const validateEmail = () => {
        if (!email.trim()) {
            setErrorMsg('Email is required');
            return false;
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
            setErrorMsg('Please enter a valid email address');
            return false;
        }
        return true;
    };

    const handleResetPassword = async () => {
        if (!validateEmail()) return;

        setIsLoading(true);
        setErrorMsg('');
        setSuccessMsg('');

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            if (error) {
                setErrorMsg(error.message);
            } else {
                setSuccessMsg('Password reset instructions have been sent to your email address.');
            }
        } catch (error: any) {
            setErrorMsg('An unexpected error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleResetPassword();
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setErrorMsg(''); // Clear error when user starts typing
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'white',
            }}
        >
            <Paper
                elevation={24}
                sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 400,
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                        Reset Password
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Enter your email address and we'll send you instructions to reset your password
                    </Typography>
                </Box>

                {/* Email Field */}
                <TextField
                    fullWidth
                    margin="normal"
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Error/Success Messages */}
                {errorMsg && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMsg}
                    </Alert>
                )}
                {successMsg && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {successMsg}
                    </Alert>
                )}

                {/* Submit Button */}
                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleResetPassword}
                    disabled={isLoading}
                    sx={{
                        mt: 3,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                    }}
                >
                    {isLoading ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Sending Instructions...
                        </>
                    ) : (
                        'Send Reset Instructions'
                    )}
                </Button>

                {/* Back to Login Link */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/login')}
                        sx={{
                            textDecoration: 'none',
                            color: 'primary.main',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        <ArrowBackIcon fontSize="small" />
                        Back to Sign In
                    </Link>
                </Box>
            </Paper>
        </Box>
    );
};

export default ForgotPassword; 